import { getIdFromObj, getIriFromObj } from 'utils/processors';

export const TASK_TREE_MAX_DEPTH = 3;

const getTaskIdFromIri = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const match = value.match(/\/workmgmt\/tasks\/(\d+)$/);
    return match ? Number(match[1]) : null;
};

const getTplTaskIdFromIri = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const match = value.match(/\/tplmgmt\/tasks\/(\d+)$/);
    return match ? Number(match[1]) : null;
};

const normalizeSiblingPosition = (task) => {
    const position = Number(task?.positionByParent);
    return Number.isFinite(position) ? position : Number.MAX_SAFE_INTEGER;
};

const getTaskName = (task) => task?.name || task?.code || '';

const getTaskLookupKey = (task, fieldName) => {
    if (typeof task?.[fieldName] !== 'string') {
        return null;
    }

    const normalizedValue = task[fieldName].trim().toLowerCase();
    return normalizedValue ? `${fieldName}:${normalizedValue}` : null;
};

const buildUniqueTaskMetaLookup = (metas = [], fieldName = '') => {
    const keyCounts = new Map();

    metas.forEach((meta) => {
        const lookupKey = getTaskLookupKey(meta?.task, fieldName);

        if (lookupKey) {
            keyCounts.set(lookupKey, (keyCounts.get(lookupKey) || 0) + 1);
        }
    });

    return metas.reduce((lookup, meta) => {
        const lookupKey = getTaskLookupKey(meta?.task, fieldName);

        if (lookupKey && keyCounts.get(lookupKey) === 1) {
            lookup.set(lookupKey, meta);
        }

        return lookup;
    }, new Map());
};

const normalizeTplTaskReference = (value) => {
    if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        typeof value?.tplReference === 'string' &&
        value.tplReference.trim()
    ) {
        return normalizeTplTaskReference(value.tplReference);
    }

    if (typeof value === 'string' && value.trim()) {
        const normalizedValue = value.trim();
        const tplTaskIdFromString = getTplTaskIdFromIri(normalizedValue);

        return tplTaskIdFromString !== null ? `/tplmgmt/tasks/${tplTaskIdFromString}` : normalizedValue;
    }

    const iri = getIriFromObj(value);
    const tplTaskIdFromIri = getTplTaskIdFromIri(iri);
    if (tplTaskIdFromIri !== null) {
        return `/tplmgmt/tasks/${tplTaskIdFromIri}`;
    }
    if (iri) {
        return iri;
    }

    const id = getIdFromObj(value);
    if (id !== undefined && id !== null && `${id}`.trim()) {
        const normalizedId = `${id}`.trim();
        const tplTaskIdFromId = getTplTaskIdFromIri(normalizedId);

        return `/tplmgmt/tasks/${tplTaskIdFromId !== null ? tplTaskIdFromId : normalizedId}`;
    }

    return null;
};

export const getTaskNodeId = (task, fallbackIndex = 0) => {
    if (typeof task?.treeNodeId === 'string' && task.treeNodeId.trim()) {
        return task.treeNodeId;
    }

    const iri = getIriFromObj(task);
    const idFromIri = getTaskIdFromIri(iri);
    if (idFromIri !== null) {
        return `task:${idFromIri}`;
    }
    if (iri) {
        return iri;
    }

    const id = getIdFromObj(task);
    if (id !== undefined && id !== null) {
        if (typeof id === 'string' && id.trim()) {
            const normalizedId = id.trim();
            const idFromStringIri = getTaskIdFromIri(normalizedId);

            return idFromStringIri !== null ? `task:${idFromStringIri}` : `task:${normalizedId}`;
        }

        return `task:${id}`;
    }

    if (typeof task?.code === 'string' && task.code.trim()) {
        return `task-code:${task.code.trim()}`;
    }

    if (typeof task?.name === 'string' && task.name.trim()) {
        return `task-name:${task.name.trim()}:${fallbackIndex}`;
    }

    return `task-index:${fallbackIndex}`;
};

export const getTaskParentRef = (task) => {
    if (typeof task?.parent?.treeNodeId === 'string' && task.parent.treeNodeId.trim()) {
        return task.parent.treeNodeId;
    }

    const parentIdFromDirectIri = getTaskIdFromIri(task?.parent);
    if (parentIdFromDirectIri !== null) {
        return `task:${parentIdFromDirectIri}`;
    }

    const parentId = getIdFromObj(task?.parent);
    if (parentId !== undefined && parentId !== null) {
        if (typeof parentId === 'string' && parentId.trim()) {
            const normalizedParentId = parentId.trim();
            const parentIdFromStringIri = getTaskIdFromIri(normalizedParentId);

            return parentIdFromStringIri !== null ? `task:${parentIdFromStringIri}` : `task:${normalizedParentId}`;
        }

        return `task:${parentId}`;
    }

    const iri = getIriFromObj(task?.parent);
    const parentIdFromObjectIri = getTaskIdFromIri(iri);
    if (parentIdFromObjectIri !== null) {
        return `task:${parentIdFromObjectIri}`;
    }
    if (iri) {
        return iri;
    }

    if (typeof task?.parent === 'number') {
        return `task:${task.parent}`;
    }

    return typeof task?.parent === 'string' && task.parent.trim() ? task.parent : null;
};

export const isRootTask = (task) => !getTaskParentRef(task);

const compareSiblingTasks = (metaA, metaB) => {
    const positionDiff = normalizeSiblingPosition(metaA.task) - normalizeSiblingPosition(metaB.task);
    if (positionDiff !== 0) {
        return positionDiff;
    }

    const nameCompare = getTaskName(metaA.task).localeCompare(getTaskName(metaB.task));
    if (nameCompare !== 0) {
        return nameCompare;
    }

    return metaA.flatIndex - metaB.flatIndex;
};

const buildTaskMetadata = (tasks = []) => {
    const sourceTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
    const metas = sourceTasks.map((task, flatIndex) => ({
        task,
        flatIndex,
        nodeId: getTaskNodeId(task, flatIndex),
        parentRef: getTaskParentRef(task)
    }));

    const metasByNodeId = new Map(metas.map((meta) => [meta.nodeId, meta]));
    const metasByTaskRef = new Map(metas.map((meta) => [meta.task, meta]));
    const uniqueMetasByCode = buildUniqueTaskMetaLookup(metas, 'code');
    const uniqueMetasByName = buildUniqueTaskMetaLookup(metas, 'name');
    const childrenByParent = new Map();

    const resolveParentMeta = (meta) => {
        if (meta.parentRef && metasByNodeId.has(meta.parentRef)) {
            return metasByNodeId.get(meta.parentRef);
        }

        const parentTask = meta?.task?.parent;
        if (!parentTask || typeof parentTask !== 'object' || Array.isArray(parentTask)) {
            return null;
        }

        const parentMetaByRef = metasByTaskRef.get(parentTask);
        if (parentMetaByRef) {
            return parentMetaByRef;
        }

        const parentCodeKey = getTaskLookupKey(parentTask, 'code');
        if (parentCodeKey && uniqueMetasByCode.has(parentCodeKey)) {
            return uniqueMetasByCode.get(parentCodeKey);
        }

        const parentNameKey = getTaskLookupKey(parentTask, 'name');
        if (parentNameKey && uniqueMetasByName.has(parentNameKey)) {
            return uniqueMetasByName.get(parentNameKey);
        }

        return null;
    };

    metas.forEach((meta) => {
        const parentMeta = resolveParentMeta(meta);
        const normalizedParentRef = parentMeta?.nodeId ?? null;
        meta.parentNodeId = normalizedParentRef;

        if (!childrenByParent.has(normalizedParentRef)) {
            childrenByParent.set(normalizedParentRef, []);
        }

        childrenByParent.get(normalizedParentRef).push(meta);
    });

    childrenByParent.forEach((siblings) => siblings.sort(compareSiblingTasks));

    return { childrenByParent };
};

// The tree grid should use stable internal node ids for grouping.
// Mixing backend path segments here makes MUI render code-like grouping keys
// instead of the task labels in the anchor column.
const resolveTreePath = (meta, inheritedPath) => [...inheritedPath, meta.nodeId];

export const sortTasksByParentPosition = (tasks = []) => {
    const { childrenByParent } = buildTaskMetadata(tasks);
    const orderedMetas = [];

    const walk = (parentNodeId = null) => {
        const siblings = childrenByParent.get(parentNodeId) || [];

        siblings.forEach((meta) => {
            orderedMetas.push(meta);
            walk(meta.nodeId);
        });
    };

    walk(null);

    return orderedMetas.map((meta) => meta.task);
};

export function applyTaskHierarchyFromTemplate(tasks = [], templateTasks = []) {
    const sourceTasks = Array.isArray(tasks) ? tasks.filter(Boolean).map((task) => ({ ...task })) : [];

    if (sourceTasks.length === 0) {
        return [];
    }

    const sourceTemplateTasks = Array.isArray(templateTasks) ? templateTasks.filter(Boolean) : [];
    if (sourceTemplateTasks.length === 0) {
        return sortTasksByParentPosition(sourceTasks);
    }

    const taskByTplReference = new Map(
        sourceTasks.map((task) => [normalizeTplTaskReference(task), task]).filter(([tplReference]) => Boolean(tplReference))
    );
    const templateParentByTplReference = new Map();

    sourceTemplateTasks.forEach((templateTask) => {
        const tplReference = normalizeTplTaskReference(templateTask);

        if (!tplReference) {
            return;
        }

        templateParentByTplReference.set(tplReference, normalizeTplTaskReference(templateTask?.parent));
    });

    sourceTasks.forEach((task) => {
        if (getTaskParentRef(task)) {
            return;
        }

        const tplReference = normalizeTplTaskReference(task);
        const parentTplReference = templateParentByTplReference.get(tplReference);
        const parentTask = parentTplReference ? taskByTplReference.get(parentTplReference) : null;

        if (parentTask && parentTask !== task) {
            task.parent = parentTask;
        }
    });

    return sortTasksByParentPosition(sourceTasks);
}

export const buildTaskTreeRows = (tasks = []) => {
    const { childrenByParent } = buildTaskMetadata(tasks);
    const treeRows = [];

    const walk = (parentNodeId = null, inheritedPath = []) => {
        const siblings = childrenByParent.get(parentNodeId) || [];

        siblings.forEach((meta) => {
            const treePath = resolveTreePath(meta, inheritedPath);

            treeRows.push({
                ...meta.task,
                id: meta.task?.id ?? meta.nodeId,
                treePath,
                treeNodeId: meta.nodeId,
                flatIndex: meta.flatIndex,
                treeDepth: Math.max(treePath.length - 1, 0),
                isRoot: parentNodeId === null
            });

            walk(meta.nodeId, treePath);
        });
    };

    walk(null);

    return treeRows;
};

export const getRootTaskRows = (tasks = []) => buildTaskTreeRows(tasks).filter((task) => task.isRoot);

export const findTaskFlatIndex = (tasks = [], targetTask = null) => {
    const targetNodeId = getTaskNodeId(targetTask);
    const matchedRow = buildTaskTreeRows(tasks).find((task) => task.treeNodeId === targetNodeId);

    return matchedRow?.flatIndex ?? -1;
};

export const collectTaskBranchIndexes = (tasks = [], targetTask = null) => {
    const treeRows = buildTaskTreeRows(tasks);
    const targetNodeId = getTaskNodeId(targetTask);
    const targetRow = treeRows.find((task) => task.treeNodeId === targetNodeId);

    if (!targetRow) {
        return [];
    }

    return treeRows
        .filter((task) => targetRow.treePath.every((segment, index) => task.treePath[index] === segment))
        .map((task) => task.flatIndex)
        .sort((indexA, indexB) => indexA - indexB);
};

export const deleteTaskBranch = (tasks = [], targetTask = null) => {
    const branchIndexes = new Set(collectTaskBranchIndexes(tasks, targetTask));

    return (Array.isArray(tasks) ? tasks : []).filter((_, flatIndex) => !branchIndexes.has(flatIndex));
};

export const getNextTaskPositionByParent = (tasks = [], parentTask = null) => {
    const parentRef = parentTask ? getTaskNodeId(parentTask) : null;
    const siblingRows = buildTaskTreeRows(tasks).filter((taskRow) => {
        const taskParentRef = getTaskParentRef(taskRow);

        if (!parentRef) {
            return !taskParentRef;
        }

        return taskParentRef === parentRef;
    });

    if (siblingRows.length === 0) {
        return 1;
    }

    return (
        siblingRows.reduce((maxPosition, taskRow) => {
            const position = Number(taskRow?.positionByParent);

            return Number.isFinite(position) && position > maxPosition ? position : maxPosition;
        }, 0) + 1
    );
};
